import React, { useState } from "react";
import {
  CheckSquare,
  Plus,
  Loader2,
  ArrowLeft,
  X,
  Clock,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { tasksApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";

interface UpcomingTask {
  _id: string;
  title: string;
  status: string;
  priority?: string;
  dueAt?: string;
}

interface TaskSubPanelProps {
  matterId: string;
  clientId: string;
  matterTitle: string;
  matterNumber?: string;
  upcomingTasks?: UpcomingTask[];
  openCount?: number;
  overdueCount?: number;
  onCreated?: () => void;
  onClose: () => void;
  onViewAll: () => void;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  todo: { label: "To Do", color: "bg-slate-100 text-slate-600" },
  doing: { label: "In Progress", color: "bg-blue-100 text-blue-700" },
  blocked: { label: "Blocked", color: "bg-red-100 text-red-700" },
};

const priorityColors: Record<string, string> = {
  urgent: "text-red-600",
  high: "text-orange-600",
  normal: "text-slate-500",
  low: "text-slate-400",
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const TaskSubPanel: React.FC<TaskSubPanelProps> = ({
  matterId,
  clientId,
  matterTitle,
  matterNumber,
  upcomingTasks = [],
  openCount = 0,
  overdueCount = 0,
  onCreated,
  onClose,
  onViewAll,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"low" | "normal" | "high" | "urgent">("normal");
  const [dueAt, setDueAt] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    setError("");
    try {
      await tasksApi.create({
        matterId,
        clientId,
        title: title.trim(),
        description: description.trim() || null,
        priority,
        dueAt: dueAt || null,
        status: "todo",
      });
      setTitle("");
      setDescription("");
      setPriority("normal");
      setDueAt("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      onCreated?.();
    } catch (err: any) {
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        "Failed to create task";
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white shrink-0">
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors md:hidden"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="h-8 w-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
          <CheckSquare className="h-4 w-4 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-800">Tasks</h3>
          <p className="text-xs text-slate-500 truncate">
            {matterNumber ? `${matterNumber} — ${matterTitle}` : matterTitle}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
        {/* Summary badges */}
        <div className="flex items-center gap-3">
          <div className="bg-slate-50 rounded-lg px-3 py-2 text-center flex-1">
            <p className="text-lg font-bold text-slate-800">{openCount}</p>
            <p className="text-[11px] text-slate-500">Open</p>
          </div>
          {overdueCount > 0 && (
            <div className="bg-red-50 rounded-lg px-3 py-2 text-center flex-1">
              <p className="text-lg font-bold text-red-700">{overdueCount}</p>
              <p className="text-[11px] text-red-600">Overdue</p>
            </div>
          )}
        </div>

        {/* Upcoming tasks */}
        {upcomingTasks.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Upcoming
            </p>
            <div className="space-y-2">
              {upcomingTasks.map((task) => (
                <div
                  key={task._id}
                  className="bg-slate-50 rounded-lg px-3 py-2.5 flex items-center gap-2.5"
                >
                  <span
                    className={`h-2 w-2 rounded-full shrink-0 ${
                      task.priority === "urgent"
                        ? "bg-red-500"
                        : task.priority === "high"
                        ? "bg-orange-500"
                        : "bg-slate-300"
                    }`}
                  />
                  <span className="text-sm text-slate-700 flex-1 truncate">
                    {task.title}
                  </span>
                  <span
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      statusLabels[task.status]?.color || "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {statusLabels[task.status]?.label || task.status}
                  </span>
                  {task.dueAt && (
                    <span className="text-[11px] text-slate-400 shrink-0 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(task.dueAt)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-slate-100" />

        {/* Create Task Form */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Create New Task
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Review discovery documents..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && title.trim()) handleCreate();
                }}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Optional details..."
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Priority
                </label>
                <SearchableSelect
                  options={[
                    { value: "low", label: "Low" },
                    { value: "normal", label: "Normal" },
                    { value: "high", label: "High" },
                    { value: "urgent", label: "Urgent" },
                  ]}
                  value={priority}
                  onChange={(v) => setPriority((v as any) || "normal")}
                  placeholder="Select priority..."
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            {success && (
              <p className="text-sm text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
                Task created successfully
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0 flex items-center justify-between">
        <button
          onClick={onViewAll}
          className="text-xs font-medium flex items-center gap-1 transition-colors"
          style={{ color: "var(--color-primary)" }}
        >
          View All Tasks
          <ExternalLink className="h-3 w-3" />
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !title.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              if (!creating)
                e.currentTarget.style.backgroundColor =
                  "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => {
              if (!creating)
                e.currentTarget.style.backgroundColor = "var(--color-primary)";
            }}
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {creating ? "Creating..." : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskSubPanel;
